import axios from "axios"

// 230726 (permintaan user): satuan input PO mengikuti MASTER satuan (tabel satuan:
// PCS=1, LSN=12, PSG=2, GRS=144, LPS=24, …). Faktor pcs per satuan di-fetch sekali
// per sesi halaman dan disimpan module-level supaya semua helper (payload, tampilan,
// overstock) memakai angka yang sama. Sebelum termuat / bila fetch gagal, fallback
// konvensi lama (LUSIN=12, PASANG=2, lainnya 1) tetap berlaku.
const factors = {}
let loadPromise = null

export const loadMasterUomFactors = () => {
  if (loadPromise) return loadPromise

  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  loadPromise = axios
    .get(`${base}/satuan`, { params: { paginate: false } })
    .then((res) => {
      const rows = Array.isArray(res?.data?.data)
        ? res.data.data
        : (res?.data?.data?.data ?? [])
      const names = []
      for (const row of rows) {
        const name = String(row?.name || "").trim().toUpperCase()
        const qty = Number(row?.qty_per_unit)
        if (!name) continue
        names.push(name)
        if (Number.isFinite(qty) && qty > 0) factors[name] = Math.max(1, Math.round(qty))
      }
      return names
    })
    .catch(() => [])

  return loadPromise
}

export const masterUomFactor = (uom) => {
  const u = String(uom || "").trim().toUpperCase()
  if (Number.isFinite(factors[u]) && factors[u] > 0) return factors[u]
  if (u === "LUSIN") return 12
  if (u === "PASANG") return 2
  return 1
}

// 290726 (permintaan user): satuan DEFAULT mengikuti ENTITAS user —
// VISION → PASANG (PSG, 2 pcs), ATALLA → LUSIN (LSN, 12 pcs).
// Master memakai KODE (LSN/PSG) sedangkan fallback lama memakai LABEL
// (LUSIN/PASANG), jadi keduanya dicoba. Bila satuan yang dicari tidak ada di
// daftar opsi yang tersedia, turun ke PCS supaya qty tidak pernah terkonversi
// ×2/×12 di produk yang tidak lazim dijual begitu.
const ENTITY_DEFAULT_UOM = {
  VISION: ['PSG', 'PASANG'],
  ATALLA: ['LSN', 'LUSIN'],
}

/**
 * Satuan default untuk entitas tertentu.
 *
 * @param {string|null} entity  entitas user (mis. dari getCurrentEntity()).
 * @param {string[]|null} options daftar satuan yang tersedia di dropdown.
 *   Null/kosong = belum termuat → tetap kembalikan kandidat pertama supaya
 *   form tidak "kedip" dari PCS ke LUSIN setelah master datang.
 * @returns {string} kode satuan; 'PCS' bila tidak ada yang cocok.
 */
export const defaultUomForEntity = (entity, options = null) => {
  const key = String(entity || '').trim().toUpperCase()
  const candidates = ENTITY_DEFAULT_UOM[key]
  if (!candidates) return 'PCS'

  if (!Array.isArray(options) || options.length === 0) {
    // Opsi belum termuat (dipakai utk INIT sinkron state). Pilih kandidat yang
    // faktornya sudah dikenal; kalau master belum termuat sama sekali, pakai label
    // legacy (LUSIN/PASANG) — faktor fallback-nya (12/2) selalu benar, sedangkan
    // kode master (LSN/PSG) berfaktor 1 sebelum master datang.
    const known = candidates.find((c) => Number.isFinite(factors[c]) && factors[c] > 0)
    return known ?? candidates[candidates.length - 1]
  }

  const available = options.map((o) => String(o || '').trim().toUpperCase())
  return candidates.find((c) => available.includes(c)) ?? 'PCS'
}

// 290726 (laporan user): penamaan satuan tidak konsisten — MASTER memakai KODE
// (LSN/PSG/GRS) sedangkan data & fallback lama memakai LABEL (LUSIN/PASANG),
// sehingga dropdown bisa menampilkan "LUSIN" sementara layar lain "LSN".
// normalizeUomCode() memetakan label lama ke kode master BILA kode itu memang ada
// di master; kalau tidak, nilainya dikembalikan apa adanya.
//
// Aman terhadap data lama: backend OrderController::uomPcsFactor() mencocokkan
// master dulu lalu fallback str_contains('LUSIN'/'PASANG'), jadi PO lama ber-uom
// "LUSIN" tetap dihitung 12 pcs walau layar menampilkannya sebagai "LSN".
const LEGACY_UOM_ALIAS = {
  LUSIN: 'LSN',
  PASANG: 'PSG',
}

export const normalizeUomCode = (uom) => {
  const u = String(uom || '').trim().toUpperCase()
  if (!u) return u

  // Sudah dikenal master (mis. LSN) → biarkan.
  if (Number.isFinite(factors[u]) && factors[u] > 0) return u

  const alias = LEGACY_UOM_ALIAS[u]
  // Hanya dipetakan kalau kode master-nya benar-benar tersedia; kalau master gagal
  // termuat, label lama dipertahankan supaya tidak menampilkan kode yang tak ada
  // di dropdown.
  if (alias && Number.isFinite(factors[alias]) && factors[alias] > 0) return alias

  return u
}
